import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    NgZone,
    OnChanges,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { ThemeOptionsWithDefault } from '@frontend/sports/common/ui/shared';
import { SportsThemedSegmentedControlDirective } from '@frontend/sports/common/ui/sports-themed-segmented-control';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { filter } from 'rxjs';

import { HeaderNavigationService } from '../../event-details-common/header-navigation/header-navigation.service';
import { FeatureTab, ScoreboardMediaTrackingService } from '../../event-details-common/scoreboard/scoreboard-media-tracking.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';

@Component({
    selector: 'ms-media-switcher',
    templateUrl: 'media-switcher.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DsSegmentedControlModule, IconCustomComponent, SportsThemedSegmentedControlDirective],
})
export class MediaSwitcherComponent implements OnChanges {
    @HostBinding('class') className = 'media-switcher';

    @Input() event: EventModel | undefined;
    @Output() mediaTabsCountChanged = new EventEmitter<number>();
    tabs: TabBarItem[];
    currentSelectedTab?: string;

    readonly segmentedControlThemeInverse: ThemeOptionsWithDefault<boolean> = {
        'giocodigitale': false,
        'premium-dark': false,
        'sports-interaction': false,
        'sportingbet-2': false,
        'default': true,
    };

    private mediaTabsCount: number;

    private ngZone = inject(NgZone);

    constructor(
        private headerNavigation: HeaderNavigationService,
        private scoreboardMediaTrackingService: ScoreboardMediaTrackingService,
        private changeDetector: ChangeDetectorRef,
        private strategy: RxStrategyProvider,
    ) {
        this.headerNavigation.selectedTab
            ?.pipe(
                filter((x) => !!(x && this.tabs)),
                takeUntilDestroyed(),
            )
            .subscribe((x) => {
                this.tabs.forEach((tab) => (tab.active = tab.id === x?.id));
                this.currentSelectedTab = x?.id.toString();
                this.changeDetector.markForCheck();
            });
    }

    ngOnChanges(): void {
        if (this.event) {
            this.headerNavigation.getTabs(this.event).then((result) => {
                this.tabs = result;
                this.changeDetector.markForCheck();

                if (this.mediaTabsCount !== this.tabs.length) {
                    this.mediaTabsCount = this.tabs.length;
                    this.mediaTabsCountChanged.emit(this.mediaTabsCount);
                }
            });
        }
    }

    selected(activeTabId: string): void {
        const activeTab = this.tabs?.find((tab) => tab.id.toString() === activeTabId);

        if (activeTab) {
            this.strategy
                .schedule(
                    () => {
                        if (this.event) {
                            this.headerNavigation.updateSelected(activeTab);

                            if (
                                activeTab.id === FeatureTab.Statistics ||
                                activeTab.id === FeatureTab.Simulation ||
                                activeTab.id === FeatureTab.Video ||
                                activeTab.id === FeatureTab.Scoreboard
                            ) {
                                this.scoreboardMediaTrackingService.trackTabsInteractionForHeader(activeTab.id, this.event);
                            }
                        }
                    },
                    { scope: this, patchZone: this.ngZone },
                )
                .subscribe();
        }
    }
}
